/**
 * Pure helpers for the record "Permission zones" popover (RBAC Model G).
 *
 * A record's effective zones are the union of the zones it is tagged into
 * directly (a `record_zone` row on the record itself) and the zones inherited
 * from its project, its bundle and -- for a custom object -- its object
 * template. Kept free of React / store so it is unit-testable.
 */

export interface RecordZoneTag {
    id: string;
    record_id: string;
    zone_id: string;
    zone_id_zone?: { name?: string | null } | null;
    record_id_record?: { name?: string | null; record_type?: string | null } | null;
}

export interface ZoneRow {
    /** Row key for the table (the zone id). */
    id: string;
    /** Zone id. */
    zoneId: string;
    zoneName: string;
    /** record_zone id of the DIRECT tag; undefined when the zone is only inherited. */
    directTagId?: string;
    /** The containers (project / bundle / object template) the zone is inherited from. */
    inheritedFrom: { id: string; name: string; recordType: string }[];
}

/** The ids a record inherits zones from: its project, bundle and object template. */
export const containerIdsOf = (record: {
    project_id?: string | null;
    bundle_id?: string | null;
    object_template_id?: string | null;
}): string[] =>
    [record?.project_id, record?.bundle_id, record?.object_template_id].filter((id): id is string =>
        Boolean(id),
    );

/**
 * Merge direct and inherited tags into one row per zone, sorted by zone name.
 * A zone both tagged directly and inherited yields ONE row carrying both facts.
 */
export const deriveZoneRows = (
    directTags: RecordZoneTag[] | null | undefined,
    inheritedTags: RecordZoneTag[] | null | undefined,
): ZoneRow[] => {
    const byZone: Record<string, ZoneRow> = {};
    const rowFor = (tag: RecordZoneTag): ZoneRow => {
        if (!byZone[tag.zone_id]) {
            byZone[tag.zone_id] = {
                id: tag.zone_id,
                zoneId: tag.zone_id,
                zoneName: tag.zone_id_zone?.name ?? tag.zone_id,
                inheritedFrom: [],
            };
        }
        return byZone[tag.zone_id];
    };
    (directTags ?? []).forEach((tag) => {
        rowFor(tag).directTagId = tag.id;
    });
    (inheritedTags ?? []).forEach((tag) => {
        rowFor(tag).inheritedFrom.push({
            id: tag.record_id,
            name: tag.record_id_record?.name ?? tag.record_id,
            recordType: (tag.record_id_record?.record_type ?? "").toLowerCase(),
        });
    });
    return Object.values(byZone).sort((a, b) =>
        a.zoneName.localeCompare(b.zoneName, undefined, { sensitivity: "base" }),
    );
};

/** Zones the record can still be tagged into: every zone without a direct tag. */
export const addableZones = <Z extends { id: string; name: string }>(
    allZones: Z[] | null | undefined,
    rows: ZoneRow[],
): Z[] => {
    const tagged = new Set(rows.filter((r) => r.directTagId).map((r) => r.zoneId));
    return (allZones ?? []).filter((zone) => !tagged.has(zone.id));
};
